import { BasketDrawer } from '@common/components/BasketDrawer/BasketDrawer';
import { calculateTotalNumberOfItemsInBasket } from '@common/components/BasketDrawer/BasketDrawer.helpers';
import { ModuleType } from '@common/components/BasketDrawer/BasketListing.types';
import { useBasketDrawerCheckoutCta } from '@common/components/BasketDrawer/hooks/useBasketDrawerCheckoutCta';
import { useModulesToRecommend } from '@common/components/BasketDrawer/hooks/useModulesToRecommend';
import { FormWrapper, InnerScroller, Modal } from '@common/components/BookingForms';
import { Spinner } from '@common/components/Spinner/Spinner';
import { StickyBarWrapper } from '@common/components/StickyBar';
import { Routes } from '@common/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from '@lang';
import { useQueryClient } from '@tanstack/react-query';
import { some } from '@utils/some';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import { useInvalidatePackagesAvailability } from '../packages';
import { useRoomExtras } from '../rooms';
import {
  useGetSpaPackage,
  useGetSpaPackageConfigGroups,
  useGetSpaPackageTimeslots,
  useGetSpaSettings,
  useInvalidateSpaPackageConfigGroups,
} from '../spa';
import { useGetSpaPackageExtras } from '../spa/hooks/useGetSpaPackageExtras';
import { useStepNavigation } from './components/StepNavigator/useStepNavigation';
import { STICKY_BAR_ID } from './constants';
import { PackageConfigurationProvider, WizardNavigationProvider } from './contexts';
import {
  DynamicPackagesConfigurationWizardDispatchProps,
  DynamicPackagesConfigurationWizardProps,
  DynamicPackagesConfigurationWizardStoreProps,
} from './DynamicPackagesConfigurationWizard.types';
import { FormSteps } from './FormSteps/FormSteps';
import { Header } from './FormSteps/FormSteps.styles';
import { usePrefetchMultiGuestAvailability } from './FormSteps/hooks/usePrefetchMultiGuestAvailability';
import { addToBasket } from './helpers/addToBasket';
import { formatDateISO, formatDateRangeISO } from './helpers/dateFormatters';
import { getExperienceSteps } from './helpers/getExperienceSteps';
import { getTablesSteps } from './helpers/getTablesSteps';
import { mapLoggedInUserToGuest } from './helpers/mapLoggedInUserToGuest';
import { FormStep, Package, PackagesFormValues, PackagesSchema, PackageType } from './types';

type DynamicPackagesConfigProps = DynamicPackagesConfigurationWizardStoreProps &
  DynamicPackagesConfigurationWizardProps &
  DynamicPackagesConfigurationWizardDispatchProps;

const PrefetchWrapper = () => {
  usePrefetchMultiGuestAvailability();
  return null;
};

export const DynamicPackagesConfigurationWizard: FC<DynamicPackagesConfigProps> = ({
  property,
  journeyParams,
  rate,
  room,
  loggedGuest,
  basketGuests,
  reservedSpaTimeSlots,
  updateBasket,
  createBasket,
  basketDetails,
  language,
}) => {
  //stay info
  const adults = journeyParams.adults;
  const children = journeyParams.children;
  const totalGuestAmount = adults + children;
  const roomId = room.id;
  const rateId = rate.id;
  const { t } = useTranslations();
  const fromDate = formatDateISO(journeyParams.fromDate);
  const toDate = journeyParams.toDate
    ? formatDateISO(journeyParams.toDate)
    : formatDateISO(journeyParams.fromDate.clone().add(1, 'days'));
  const [isModalOpen, setIsModalOpen] = useState(true);
  const queryClient = useQueryClient();

  //get data for the package
  const packageData: Package | undefined = rate.package ?? undefined;
  const isFetching = false;
  const isError = !packageData;
  const mappedPackageData = {
    ...packageData,
    experiences: (packageData?.experiences || []).map((exp) => ({
      ...exp,
      dayNumberRange: exp.dayNumberRange || [exp.dayNumber],
    })),
  };

  const stayDuration =
    mappedPackageData && mappedPackageData.numberOfNights !== 1
      ? mappedPackageData.numberOfDays ||
        moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days')
      : moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days');

  // A package is "repeating" when it's designed for 1 night but the customer books multiple nights
  // experiences from day 0/1 repeat across the stay so experiences mapped to day 0 are available on all days excluding checkout
  // experiences mapped to day 1 are available on all days except day 0
  // A package is "flexible" when it has multiple nights with experiences mapped to specific days
  const packageType =
    mappedPackageData?.numberOfNights === 1 ? PackageType.REPEATING : PackageType.FLEXIBLE;
  const isRepeatedFlexibleEnabled = useFeatureIsOn('feature.package-repeated-flexible');

  const { data: availableExtras } = useRoomExtras({
    ...journeyParams,
    fromDate,
    toDate,
    roomId,
    rateId,
    language,
  });

  const { data: spaDay } = useGetSpaPackage(
    property.id,
    Number(mappedPackageData?.meta?.entityId),
    language,
    true
  );

  const { data: timeslots } = useGetSpaPackageTimeslots(
    property.id,
    Number(mappedPackageData?.meta?.entityId),
    formatDateISO(moment(journeyParams.fromDate)),
    totalGuestAmount,
    stayDuration
  );

  const { data: spaExtras = [] } = useGetSpaPackageExtras(
    property.id,
    Number(mappedPackageData?.meta?.entityId),
    language,
    formatDateISO(moment(journeyParams.fromDate)),
    true
  );

  const timeslot = Object.values(timeslots || [])?.[0]?.[0];
  const datesArray = formatDateRangeISO(journeyParams.fromDate, stayDuration);
  datesArray.forEach((date) => {
    useGetSpaPackageConfigGroups(
      spaDay?.configurationGroups,
      property.id,
      Number(mappedPackageData?.meta?.entityId),
      date,
      totalGuestAmount,
      timeslot?.startTime,
      timeslot?.endTime
    );

    useGetSpaPackageConfigGroups(
      spaDay?.configurationGroups,
      property.id,
      Number(mappedPackageData?.meta?.entityId),
      date,
      1,
      timeslot?.startTime,
      timeslot?.endTime
    );
  });

  //route handling
  const router = useRouter();
  useEffect(() => {
    if (isError) {
      router.push({
        pathname: Routes.RATE_BOOKING,
        query: { id: rateId, bookingStep: 'guestDetailsStep', room: roomId },
      });
    }
  }, [isError]);

  //form management
  const form = useForm<PackagesFormValues>({
    resolver: zodResolver(PackagesSchema),
  });
  const { setValue, watch } = form;

  //form submission
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showConfirmation = () => {
    setIsModalOpen(false);
    setIsSubmitting(false);
    setIsConfirmationVisible(true);
    useInvalidatePackagesAvailability(queryClient);
    useInvalidateSpaPackageConfigGroups(queryClient);
  };
  const hideConfirmation = () => {
    router.push({
      pathname: Routes.RATE_DETAIL,
      query: {
        id: rate.id,
      },
    });
    setIsConfirmationVisible(false);
  };

  const bookingDetails = watch();
  //step management
  const { steps: experienceSteps, includedExperiences } = getExperienceSteps(
    mappedPackageData?.experiences || [],
    stayDuration,
    packageType
  );

  const selectedExperiences = watch('selectedExperiences');

  useEffect(() => {
    if (includedExperiences && !selectedExperiences) {
      setValue('selectedExperiences', includedExperiences);
    }
  }, [includedExperiences, selectedExperiences]);

  const allFormSteps: FormStep[] = [
    {
      step: 'guestDetailsStep',
      type: 'rooms',
    },
    ...(some(availableExtras) ? [{ step: 'roomExtrasStep', type: 'rooms' }] : []),
    ...experienceSteps,
    ...(some(spaExtras) ? [{ step: 'spaExtrasStep', type: 'spa' }] : []),
    ...getTablesSteps(mappedPackageData?.restaurants || [], packageType, stayDuration),
  ];
  const { stepNavigatorComponent, goToNextStep, isLastStep, currentFormStep } = useStepNavigation(
    rate.id || '',
    packageData,
    allFormSteps
  );

  const experienceDayOffsetsByGroupId = Object.fromEntries(
    (mappedPackageData?.experiences || [])
      .filter((experience) => experience.meta?.entityId !== undefined)
      .map((experience) => [String(experience.meta?.entityId), experience.dayNumber || 0])
  );

  const handleAddToBasket = async () => {
    setIsSubmitting(true);
    try {
      await addToBasket({
        createBasket,
        updateBasket,
        bookingDetails,
        roomRateEntity: rate,
        roomId,
        children,
        childAges: journeyParams.childAges,
        fromDate,
        toDate,
        property,
        language,
        basketDetails,
        showConfirmation,
        spaPackageId: mappedPackageData?.meta?.entityId || '',
        spaOpenTime: timeslot?.startTime || '',
        spaCloseTime: timeslot?.endTime || '',
        maxGuestsPerExperience: spaDay?.numberOfGuests || totalGuestAmount,
        availableExtras: availableExtras || [],
        loggedInUserGuestData: mapLoggedInUserToGuest(loggedGuest),
        packageType: packageType,
        promoRateCode: journeyParams.promoRateCode,
        isRepeatedFlexibleEnabled,
        stayDuration,
        experienceDayOffsetsByGroupId,
      });
    } catch {
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const packageConfigValue = {
    property,
    language,
    journeyParams,
    stayDuration,
    totalGuestAmount,
    packageType,
    spaPackageId: mappedPackageData?.meta?.entityId || '',
    spaOpenTime: timeslot?.startTime || '',
    spaCloseTime: timeslot?.endTime || '',
    rate,
    room,
    availableExtras,
    basketGuests: basketGuests || [],
    loggedInGuest: loggedGuest,
    reservedSpaTimeSlots: reservedSpaTimeSlots || [],
    isRepeatedFlexibleEnabled,
  };

  const wizardNavigationValue = {
    currentFormStep,
    allFormSteps,
    handleNextStep: () => {
      if (!isLastStep) {
        goToNextStep();
      } else {
        handleAddToBasket();
      }
    },
    isLastStep,
    stickyBarId: STICKY_BAR_ID,
  };

  const formStepsProps = {
    form,
    isSubmitting,
    addToBasket: handleAddToBasket,
  };

  //basket drawer
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;
  const showBasketDrawer = isConfirmationVisible && basketDetails;

  const modules = Object.keys(property.modulesExcludingDisabled);

  const checkoutCta = useBasketDrawerCheckoutCta();

  const modulesToRecommend = useModulesToRecommend({
    modules: modules as ModuleType[],
    items: basketDetails?.items ?? [],
  });

  return (
    <div>
      {showBasketDrawer && (
        <BasketDrawer
          currencyCode={basketDetails.total.currencyCode}
          numberOfItems={calculateTotalNumberOfItemsInBasket(basketDetails.items)}
          open={true}
          onClose={hideConfirmation}
          title={t('basketDrawer.title')}
          totalCost={basketDetails.total.amount}
          items={basketDetails.items}
          modules={modulesToRecommend}
          hideArrivalTimes={hideArrivalTimes}
          {...checkoutCta}
        />
      )}
      <Modal isVisible={isSubmitting || (isModalOpen && !isError)}>
        <>
          <Spinner showDelayMessage isActive={isSubmitting || (isFetching && !mappedPackageData)} />
          <Header>{stepNavigatorComponent}</Header>
          <InnerScroller>
            <FormWrapper>
              <PackageConfigurationProvider value={packageConfigValue}>
                <WizardNavigationProvider value={wizardNavigationValue}>
                  <FormProvider {...form}>
                    <PrefetchWrapper />
                    <FormSteps {...formStepsProps} />
                  </FormProvider>
                </WizardNavigationProvider>
              </PackageConfigurationProvider>
            </FormWrapper>
          </InnerScroller>
        </>
      </Modal>
      <StickyBarWrapper id={STICKY_BAR_ID} zIndex={9999} />
    </div>
  );
};
